import { useState, useMemo } from 'react'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { AlertCircle } from 'lucide-react'
import { SalaryCalculationService } from '@/services/salaryCalculationService'
import { formatSEK, formatPercentFromWhole } from '@/lib/formatters'
import { safeParseNumber } from '@/lib/validators'
import { DataCard } from '@/components/common'
import type { Settings } from '@/stores/configStore'
import type { TipsHandlingMode } from '@/types'

function getTipsDescription(feeOnTips: TipsHandlingMode | boolean): string {
  if (feeOnTips === 'fleet_commission' || feeOnTips === true) {
    return 'Åkaravgift på dricks'
  }
  if (feeOnTips === 'separate_untaxed') {
    return 'Separat oskattat'
  }
  return '100% till förare'
}

interface CalculationPreviewProps {
  settings: Settings
  taxTableLoaded: boolean
}

export function CalculationPreview({
  settings,
  taxTableLoaded
}: CalculationPreviewProps) {
  // Example values for live preview
  const [exampleValues, setExampleValues] = useState({
    bruttoinkort: 10000,
    nettoinkort: 7500,
    dricks: 500
  })

  // Store raw example inputs to allow empty fields during editing
  const [rawExampleInputs, setRawExampleInputs] = useState({
    bruttoinkort: '10000',
    nettoinkort: '7500',
    dricks: '500'
  })

  const handleExampleChange = (field: keyof typeof exampleValues, value: string) => {
    setRawExampleInputs(prev => ({ ...prev, [field]: value }))
    const numericValue = value === '' ? 0 : safeParseNumber(value)
    setExampleValues(prev => ({ ...prev, [field]: numericValue }))
  }

  // Calculate live preview
  const calculation = useMemo(() => {
    if (settings.taxCalculationMethod === 'table' && !taxTableLoaded) {
      return {
        grossIncome: exampleValues.bruttoinkort,
        vat: 0,
        netIncome: exampleValues.nettoinkort,
        netAfterVat: 0,
        shareableAmount: 0,
        fleetCut: 0,
        driverShare: 0,
        vacationPay: 0,
        grossSalary: 0,
        employerTaxAmount: 0,
        totalEmployerCost: 0,
        taxAmount: 0,
        effectiveTaxRate: 0,
        netSalary: 0,
        tips: exampleValues.dricks,
        feeOnTips: settings.feeOnTips,
        untaxedTips: 0,
        amountToPay: 0,
        isLoading: true
      }
    }

    const config = {
      driverRevenueShare: settings.driverRevenueShare / 100,
      vacationPayRate: settings.vacationPayRate / 100,
      vatRate: settings.vatRate / 100,
      taxRate: settings.taxRate / 100,
      feeOnTips: settings.feeOnTips,
      taxCalculationMethod: settings.taxCalculationMethod ?? 'fixed',
      taxTableName: settings.taxTableName ?? 'skattetabell31',
      taxColumn: settings.taxColumn ?? 1,
      employerTaxRate: settings.employerTaxRate / 100,
      revenueShareMode: settings.revenueShareMode ?? 'single',
      revenueShareTiers: settings.revenueShareTiers
    }

    const aggregatedInput = {
      grossEarnings: exampleValues.bruttoinkort,
      netEarnings: exampleValues.nettoinkort,
      tips: exampleValues.dricks,
      cashReceived: 0
    }

    const result = SalaryCalculationService.calculateAggregatedSalary(aggregatedInput, config, [])

    const netAfterVat = exampleValues.nettoinkort - result.vatAmount

    return {
      grossIncome: exampleValues.bruttoinkort,
      vat: result.vatAmount,
      netIncome: exampleValues.nettoinkort,
      netAfterVat: netAfterVat,
      shareableAmount: result.shareableAmount,
      fleetCut: result.fleetCommission,
      driverShare: result.afterFleetCommission,
      vacationPay: result.vacationPay,
      grossSalary: result.finalGrossSalary,
      employerTaxAmount: result.employerTaxAmount,
      totalEmployerCost: result.totalEmployerCost,
      taxAmount: result.taxAmount,
      effectiveTaxRate: result.effectiveTaxRate,
      netSalary: result.finalNetSalary,
      tips: exampleValues.dricks,
      feeOnTips: settings.feeOnTips,
      untaxedTips: result.untaxedTips,
      amountToPay: result.amountToPay,
      isLoading: false
    }
  }, [settings, exampleValues, taxTableLoaded])

  return (
    <DataCard>
      <div className="space-y-5">
        {/* Input Section */}
        <div className="space-y-3">
          <h4 className="font-semibold text-sm">Exempel Värden</h4>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="example-brutto" className="text-sm">Intäkt för resor (kr)</Label>
              <Input
                id="example-brutto"
                type="number"
                value={rawExampleInputs.bruttoinkort}
                onChange={(e) => handleExampleChange('bruttoinkort', e.target.value)}
                min="0"
                step="100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="example-netto" className="text-sm">Nettoinkört (kr)</Label>
              <Input
                id="example-netto"
                type="number"
                value={rawExampleInputs.nettoinkort}
                onChange={(e) => handleExampleChange('nettoinkort', e.target.value)}
                min="0"
                step="100"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="example-dricks" className="text-sm">Dricks (kr)</Label>
              <Input
                id="example-dricks"
                type="number"
                value={rawExampleInputs.dricks}
                onChange={(e) => handleExampleChange('dricks', e.target.value)}
                min="0"
                step="50"
              />
            </div>
          </div>
        </div>

        <Separator className="my-4" />

        {/* Calculation Results */}
        <div className="space-y-3">
          <h4 className="font-semibold text-sm">Beräkningsresultat</h4>

          {calculation.isLoading && (
            <div className="p-3 bg-muted/20 rounded-lg border border-border/50">
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                <span className="text-sm text-muted-foreground">Laddar skattetabell...</span>
              </div>
            </div>
          )}

          <div className="space-y-2 text-sm">
            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Moms ({formatPercentFromWhole(settings.vatRate)})</span>
              <span className="font-medium tabular-nums">{formatSEK(calculation.vat)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Netto efter moms</span>
              <span className="font-medium tabular-nums">{formatSEK(calculation.netAfterVat)}</span>
            </div>

            {calculation.tips > 0 && (
              <div className="p-2.5 rounded-lg border border-border/50 bg-muted/20 my-2">
                <div className="flex justify-between items-center">
                  <span className="font-medium">Dricks</span>
                  <span className="font-medium tabular-nums">{formatSEK(calculation.tips)}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {getTipsDescription(settings.feeOnTips)}
                </p>
              </div>
            )}

            <Separator className="my-2" />

            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Delbar summa</span>
              <span className="font-medium tabular-nums">{formatSEK(calculation.shareableAmount)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Åkaravgift</span>
              <span className="font-medium text-muted-foreground tabular-nums">-{formatSEK(calculation.fleetCut)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Förarandel</span>
              <span className="font-medium tabular-nums">{formatSEK(calculation.driverShare)}</span>
            </div>
            <div className="flex justify-between items-center py-1.5">
              <span className="text-muted-foreground">Semesterersättning</span>
              <span className="font-medium tabular-nums">{formatSEK(calculation.vacationPay)}</span>
            </div>

            <Separator className="my-2" />

            <div className="flex justify-between items-center p-2.5 bg-primary/5 rounded-lg border border-primary/10">
              <span className="font-semibold">Bruttolön</span>
              <span className="text-lg font-bold tabular-nums">{formatSEK(calculation.grossSalary)}</span>
            </div>

            {/* Employer Tax */}
            <div className="space-y-1.5 p-2.5 bg-muted/20 rounded-lg border border-border/50 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">Arbetsgivaravgift ({formatPercentFromWhole(settings.employerTaxRate)})</span>
                <span className="font-medium tabular-nums">{formatSEK(calculation.employerTaxAmount)}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                <span className="font-semibold">Total lönekostnad</span>
                <span className="font-bold tabular-nums">{formatSEK(calculation.totalEmployerCost)}</span>
              </div>
            </div>

            {/* Tax */}
            <div className="space-y-1.5 p-2.5 bg-muted/20 rounded-lg border border-border/50 mt-2">
              <div className="flex justify-between items-center">
                <span className="text-sm">
                  {settings.taxCalculationMethod === 'table'
                    ? `Skatteavdrag (Tabell ${settings.taxTableName?.replace(/\D/g, '') ?? '31'})`
                    : `Skatteavdrag (${formatPercentFromWhole(settings.taxRate)})`
                  }
                </span>
                <span className="font-medium text-muted-foreground tabular-nums">-{formatSEK(calculation.taxAmount)}</span>
              </div>
              <Separator />
              <div className="flex justify-between items-center">
                {settings.feeOnTips === 'separate_untaxed' ? (
                  <>
                    <span className="font-semibold">Lön efter skatt</span>
                    <span className="font-bold text-primary tabular-nums">{formatSEK(calculation.netSalary)}</span>
                  </>
                ) : (
                  <>
                    <span className="font-semibold">Att utbetala</span>
                    <span className="font-bold text-primary tabular-nums">{formatSEK(calculation.amountToPay)}</span>
                  </>
                )}
              </div>
            </div>

            {/* Untaxed Tips Final Payout */}
            {settings.feeOnTips === 'separate_untaxed' && calculation.untaxedTips > 0 && (
              <div className="mt-3 space-y-2 p-2.5 bg-muted/20 rounded-lg border border-border/50">
                <div className="space-y-1.5">
                  <div className="flex justify-between items-center p-2 bg-background rounded border border-border/30">
                    <span className="text-sm font-medium">Lönedel (skattepliktig)</span>
                    <span className="font-bold tabular-nums">{formatSEK(calculation.netSalary)}</span>
                  </div>
                  <div className="flex justify-between items-center p-2 bg-background rounded border border-border/30">
                    <span className="text-sm font-medium">Oskattat dricks</span>
                    <span className="font-bold tabular-nums">+{formatSEK(calculation.untaxedTips)}</span>
                  </div>
                  <Separator />
                  <div className="flex justify-between items-center p-2.5 bg-primary/5 rounded border border-primary/10">
                    <span className="font-bold">Att utbetala</span>
                    <span className="text-xl font-bold text-primary tabular-nums">{formatSEK(calculation.amountToPay)}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {calculation.netAfterVat < 0 && (
            <Alert variant="destructive" className="mt-3">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Netto efter moms blir negativt. Kontrollera att bruttoinkört är större än nettoinkört.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </DataCard>
  )
}
